import { useQuery } from "@tanstack/react-query";

import { type SourceTypeFilter, listSources } from "@/lib/sources";

// Per-tab counts (totals only — `limit=1` keeps the payload tiny). Kept as
// one distinct `useQuery` call per tab under their existing
// `["sources-count", …]` keys (no `useQueries` collapse) so query identity
// and cache entries are unchanged from when this lived inline in the route
// container.
export function useSourceCounts(scopeKey = "default"): (value: SourceTypeFilter) => number {
  const uploadCountQuery = useQuery({
    queryKey: ["sources-count", scopeKey, "upload"] as const,
    queryFn: () => listSources({ type: "upload", limit: 1 }),
    select: (page) => page.total,
  });
  const urlCountQuery = useQuery({
    queryKey: ["sources-count", scopeKey, "url"] as const,
    queryFn: () => listSources({ type: "url", limit: 1 }),
    select: (page) => page.total,
  });
  const notionCountQuery = useQuery({
    queryKey: ["sources-count", scopeKey, "notion"] as const,
    queryFn: () => listSources({ type: "notion", limit: 1 }),
    select: (page) => page.total,
  });
  const s3CountQuery = useQuery({
    queryKey: ["sources-count", scopeKey, "s3"] as const,
    queryFn: () => listSources({ type: "s3", limit: 1 }),
    select: (page) => page.total,
  });
  const shortcutCountQuery = useQuery({
    queryKey: ["sources-count", scopeKey, "shortcut"] as const,
    queryFn: () => listSources({ type: "shortcut", limit: 1 }),
    select: (page) => page.total,
  });
  const postmanCountQuery = useQuery({
    queryKey: ["sources-count", scopeKey, "postman"] as const,
    queryFn: () => listSources({ type: "postman", limit: 1 }),
    select: (page) => page.total,
  });
  const selfCountQuery = useQuery({
    queryKey: ["sources-count", scopeKey, "self"] as const,
    queryFn: () => listSources({ type: "self", limit: 1 }),
    select: (page) => page.total,
  });
  const chatCountQuery = useQuery({
    queryKey: ["sources-count", scopeKey, "chat"] as const,
    queryFn: () => listSources({ type: "chat", limit: 1 }),
    select: (page) => page.total,
  });
  const jiraCountQuery = useQuery({
    queryKey: ["sources-count", scopeKey, "jira"] as const,
    queryFn: () => listSources({ type: "jira", limit: 1 }),
    select: (page) => page.total,
  });

  return (value: SourceTypeFilter): number => {
    if (value === "upload") return uploadCountQuery.data ?? 0;
    if (value === "url") return urlCountQuery.data ?? 0;
    if (value === "notion") return notionCountQuery.data ?? 0;
    if (value === "s3") return s3CountQuery.data ?? 0;
    if (value === "shortcut") return shortcutCountQuery.data ?? 0;
    if (value === "postman") return postmanCountQuery.data ?? 0;
    if (value === "self") return selfCountQuery.data ?? 0;
    if (value === "chat") return chatCountQuery.data ?? 0;
    if (value === "jira") return jiraCountQuery.data ?? 0;
    return 0;
  };
}
